import { Injectable, Inject } from '@angular/core';
import { HttpHeaders } from '@angular/common/http';
import { isPlatformBrowser } from '@angular/common';
import { PLATFORM_ID } from '@angular/core';
import { KeycloakAuthService } from './keycloak-auth.service';
import { GraphData } from '../models/studentProfileModels';

@Injectable({
  providedIn: 'root'
})
export class HttpHelperService {
  constructor(
    private keycloakService: KeycloakAuthService,
    @Inject(PLATFORM_ID) private platformId: object
  ) {}

  /**
   * Generates HTTP headers with Authorization token for API requests.
   * @returns HttpHeaders with Content-Type, Accept, and Authorization (if available).
   */
  getHeaders(): HttpHeaders {
    let headers = new HttpHeaders({
      'Content-Type': 'application/json',
      'Accept': 'application/json'
    });

    if (isPlatformBrowser(this.platformId)) {
      const token = this.keycloakService.getAccessToken();
      if (token) {
        headers = headers.set('Authorization', `Bearer ${token}`);
      }
    }

    return headers;
  }

  /**
   * Calculates the percentage change between current and previous data.
   * @param current Current period data
   * @param previous Previous period data
   * @returns Object containing percentage change, CSS class, and icon
   */
  calculateChange(current: { labels: string[], data: number[] }, previous: { labels: string[], data: number[] }) {
    const currentValue = current.data[0] || 0;
    const previousValue = previous.data[0] || 0;
    let percentage = '0.0';
    let changeClass = '';
    let icon = '';

    if (previousValue === 0 && currentValue === 0) {
      percentage = '0.0';
    } else if (previousValue === 0) {
      percentage = 'N/A'; // Avoid division by zero
    } else {
      const change = ((currentValue - previousValue) / (previousValue + currentValue)) * 100;
      percentage = change.toFixed(1);
      changeClass = change >= 0 ? 'change-increase' : 'change-decrease';
      icon = change >= 0 ? 'arrow_upward' : 'arrow_downward';
    }
    return { class: changeClass, icon, percentage };
}

  /**
   * Sums the data values in a GraphData object.
   * @param data GraphData object
   * @returns Sum of data values
   */
  sumData(data: GraphData): number {
    return data.data.reduce((sum, value) => sum + value, 0);
  }

  /**
   * Transforms GraphData into chart-compatible format.
   * @param data GraphData object
   * @returns Array of objects with category and value properties
   */
  transformChartData(data: GraphData): any[] {
    return data.labels.map((label, index) => ({ category: label, value: data.data[index] }));
  }
}